import { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { getRandomElement, musicIdArray } from "@/utils/getRandomSong";
import { fetchedMusicType } from "@/components/apps/MusicPlayer";
import useSettingsStore from "@/store/SettingsStore";
import useMusicStore from "@/store/MusicStore";

export type MusicType = {
  id: string;
  title: string;
  artist: string;
  url: string;
  cover: string;
};

export const useMusicPlayer = () => {
  const isSongFetching = useMusicStore(state => state.isSongFetching)
  const musicHistory = useMusicStore(state => state.musicHistory)
  const isPlaying = useMusicStore(state => state.isPlaying)
  const playerRef = useMusicStore(state => state.playerRef)
  const songIndex = useMusicStore(state => state.songIndex)
  const progress = useMusicStore(state => state.progress)

  const { addMusic, setIsPlaying, setIsSongFetching, setSongIndex, setDuration,setProgress } = useMusicStore(state => state.actions)

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const notificationOnMusicPlay = useSettingsStore(state => state.notificationOnMusicPlay)

  const getSongToaster = useCallback((cover: string, title: string, artist: string) => {
    if (notificationOnMusicPlay) {
      toast(
        <div className="w-60">
          <div className="flex items-center space-x-2">
            <img src={cover} alt="Cover" className="w-12 h-12 rounded-full mr-2" />
            <div>
              <p className="font-semibold line-clamp-1">{title}</p>
              <p className="text-sm text-zinc-400 line-clamp-1">{artist}</p>
            </div>
          </div>
        </div>
      )
    }
  }, [notificationOnMusicPlay])

  // Fetch a random song
  const fetchRandomSong = useCallback(async () => {
    if (isSongFetching) return
    setIsSongFetching(true);
    try {
      let musicId;

      if (musicHistory.length === 0) {
        // First song should always be random
        musicId = getRandomElement(musicIdArray);
      } else {
        musicId = Math.random() > 0.5 ? musicHistory[musicHistory.length - 1].id : getRandomElement(musicIdArray);
      }
      const serverUri = import.meta.env.VITE_SERVER_URI || "";
      const response = await axios.get(`${serverUri}/music/suggestions/${musicId}`);
      const track: fetchedMusicType = getRandomElement(response.data.data);
      const songData = {
        id: track.id,
        title: track.name,
        artist: track.artists.primary[0].name,
        url: track.downloadUrl[1].url,
        cover: track.image[1].url || "https://via.placeholder.com/150",
      };
      if (!musicHistory.some((song) => song.id === songData.id)) {
        const newIndex = musicHistory.length; // New song will be at the end
        addMusic(songData);
        setSongIndex(newIndex);
      } else {
        // Optionally, if you want to switch to the existing song:
        const existingIndex = musicHistory.findIndex((song) => song.id === songData.id);
        setSongIndex(existingIndex);
      }
      if (isPlaying) getSongToaster(songData.cover, songData.title, songData.artist);
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Error fetching song");
      console.error("Error fetching song:", error);
    } finally {
      setIsSongFetching(false);
    }
  }, [addMusic, getSongToaster, isPlaying, isSongFetching, musicHistory, setIsSongFetching, setSongIndex])

  // Play/Pause functionality
  const togglePlayPause = async () => {
    console.log("first")
    if (playerRef && playerRef.current) {
      if (isPlaying) {
        playerRef.current.pause();
      } else {
        playerRef.current.play();
        setProgress((playerRef.current.currentTime / playerRef.current.duration) * 100 || 0);
        getSongToaster(musicHistory[songIndex].cover, musicHistory[songIndex].title, musicHistory[songIndex].artist)
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle progress updates
  const handleTimeUpdate = useCallback(() => {
    if (playerRef && playerRef.current) {
      const currentTime = playerRef.current.currentTime;
      setProgress((currentTime / playerRef.current.duration) * 100 || 0);
    }
  }, [playerRef, setProgress])

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (playerRef && playerRef.current) {
      const seekTime = (Number(e.target.value) / 100) * playerRef.current.duration;
      playerRef.current.currentTime = seekTime;
      setProgress(Number(e.target.value));
    }
  };

  // Play next random song when the current song ends
  const handleSongEnd = useCallback(async () => {
    try {
      await fetchRandomSong();
    } catch (error) {
      console.error("Error fetching next song:", error);
    }
  }, [fetchRandomSong]);

  useEffect(() => {
    const timeoutId = debounceTimeout.current;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {

    const audio = playerRef && playerRef.current;
    const updateDuration = () => setDuration(audio?.duration || 0);

    if (audio) {
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", updateDuration);
      audio.addEventListener("ended", handleSongEnd); // Play next song on end
    }

    return () => {
      if (audio) {
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", updateDuration);
        audio.removeEventListener("ended", handleSongEnd);
      }
    };
  }, [handleSongEnd, handleTimeUpdate, playerRef, setDuration]);

  const playPrev = () => {
    if (playerRef)
      if (progress > 1) {
        setProgress(0);
        if (playerRef.current) {
          playerRef.current.currentTime = 0;
        }
      } else {
        if (songIndex === 0) {
          playerRef.current?.pause()
        } else {
          const prevIndex = (songIndex - 1) % musicHistory.length;
          setSongIndex(prevIndex);
          if (isPlaying) getSongToaster(musicHistory[prevIndex].cover, musicHistory[prevIndex].title, musicHistory[prevIndex].artist)
        }
      }
  }

  const playNext = () => {
    if (songIndex >= musicHistory.length - 1) {
      handleSongEnd()
    } else {
      const nextIndex = songIndex + 1
      setSongIndex(nextIndex);
      if (isPlaying) getSongToaster(musicHistory[nextIndex].cover, musicHistory[nextIndex].title, musicHistory[nextIndex].artist)
    }
  };

  return {
    togglePlayPause,
    handleSeek,
    getSongToaster,
    playPrev,
    playNext,
    fetchRandomSong
  };
};
